import {
  Button,
  ButtonProps,
  Checkbox as MuiCheckbox,
  CheckboxProps as MuiCheckboxProps,
  CircularProgress,
  FormControlLabel,
  FormControlLabelProps,
  IconButton,
  InputAdornment,
  Switch as MuiSwitch,
  SwitchProps as MuiSwitchProps,
  TextField,
  TextFieldProps,
  Typography,
} from "@material-ui/core";
import {
  KeyboardDateTimePicker,
  KeyboardDateTimePickerProps,
} from "@material-ui/pickers";
import {camelCase, get} from "lodash";
import React, {
  BaseSyntheticEvent,
  ComponentType,
  FC,
  ReactNode,
  useContext,
  useMemo,
  useState,
} from "react";
import {
  Controller,
  RegisterOptions,
  UseFormReturn,
  useFormState,
} from "react-hook-form";
import clsx from "clsx";
import {Visibility, VisibilityOff} from "@material-ui/icons";
import {isNonEmptyValue} from "tools/validations/isNonEmptyValue";
import useStyles from "./styles";

export const FormContext = React.createContext({});

type FormData = BaseSyntheticEvent<Record<string, any>, any, any>;

export type InputProps = Omit<TextFieldProps, "label" | "variant"> & {
  rules?: RegisterOptions;
  label?: ReactNode | null;
  uppercase?: boolean;
  endAdornment?: ReactNode | null;
  showHelperText?: boolean;
  variant?: any;
  shouldShrinkOnValue?: boolean;
  shouldWarn?: boolean;
  noDefaultStyles?: boolean;
};

type SwitchProps = MuiSwitchProps & {
  label?: ReactNode;
  labelClassName?: string;
};

export type CheckboxProps = MuiCheckboxProps & {
  label?: ReactNode;
  formControlLabelProps?: Omit<Omit<FormControlLabelProps, "control">, "label">;
  shouldUsePrimaryColor?: boolean;
};

export type DatePickerProps = Omit<
  KeyboardDateTimePickerProps,
  "value" | "onChange" | "label"
> & {
  onChange?: (date: Date | null) => void;
  rules?: RegisterOptions;
  label?: ReactNode;
  format?: string;
};

export type ActionButtonProps = ButtonProps & {
  formState?: UseFormReturn["formState"];
  text: ReactNode;
  showLoader?: boolean;
  ButtonComponent?: ComponentType<ButtonProps>;
  allowClean?: boolean;
};

export type ActionsProps = {
  className?: string;
  style?: React.CSSProperties;
  isLeftAction?: boolean;
};
export type ClassNames = "title" | "subtitle";
export type FormProps = {
  form: UseFormReturn<any>;
  onSubmit: (formData?: FormData) => null | Promise<void>;
  title?: string;
  subTitle?: string;
  className?: string;
  style?: Record<string, number | string>;
  name?: string;
  id?: string;
  noDefaultStyles?: boolean;
  classes?: Partial<Record<ClassNames, string>>;
};

export type PasswordInputProps = Omit<InputProps, "multiline"> & {
  shouldEllipsis?: boolean;
  showToggle?: boolean;
  defaultVisible?: boolean;
  toggleIconClassname?: string;
};

export const PasswordInput = (props: PasswordInputProps) => {
  const classes = useStyles();
  const {
    InputProps = {},
    shouldEllipsis,
    showToggle = true,
    defaultVisible = false,
    toggleIconClassname,
  } = props;
  const [showPassword, setShowPassword] = useState(defaultVisible);
  const toggleIcon = showToggle ? (
    <IconButton
      aria-label="toggle password visibility"
      onClick={() => {
        setShowPassword((prev) => !prev);
      }}
    >
      {showPassword ? (
        <Visibility className={toggleIconClassname} />
      ) : (
        <VisibilityOff className={toggleIconClassname} />
      )}
    </IconButton>
  ) : undefined;

  return (
    <BasicInput
      {...props}
      type={showPassword ? "text" : "password"}
      InputProps={{
        ...InputProps,
        classes: {
          ...(InputProps?.classes || {}),
          input: clsx(InputProps.classes?.input, {
            [classes.ellipsis]: shouldEllipsis ? showPassword : false,
          }),
        },
        endAdornment: (
          <InputAdornment position="end">
            {toggleIcon}
            {props.InputProps?.endAdornment}
          </InputAdornment>
        ),
      }}
    />
  );
};

export const Input: FC<InputProps> = (props) => {
  const classes = useStyles();
  const {
    label,
    rules,
    variant = "outlined",
    name = "",
    required = true,
    showHelperText = false,
    uppercase = false,
    shouldShrinkOnValue,
    helperText: propsHelperText,
    shouldWarn,
    className,
    ...rest
  } = props;
  const [isFocused, setIsFocused] = useState(false);
  const form = useContext(FormContext) as UseFormReturn;

  const {control} = form;
  const id = name || camelCase(label?.toString() || "");
  const {errors} = useFormState({control, name: id});
  const error = get(errors || {}, id); // get nested values splitted by dot. For example 'company.name'
  const helperText = (() => {
    if (!showHelperText) {
      return;
    }
    if (propsHelperText) {
      return propsHelperText;
    }
    return error?.message;
  })();
  const register = form.register(id, {
    required,
    ...rules,
    validate: rules?.validate ?? (required ? isNonEmptyValue : undefined),
  });
  const value = form.watch(id);

  const InputLabelProps = rest?.InputLabelProps || {};
  return (
    <TextField
      key={id}
      id={id}
      required={required}
      variant={variant || "outlined"}
      label={label}
      fullWidth
      error={Boolean(error)}
      helperText={helperText}
      {...rest}
      {...register}
      InputProps={{
        ...rest.InputProps,
        onFocus: (e) => {
          setIsFocused(true);
          rest.InputProps?.onFocus?.(e);
        },
        onBlur: (e) => {
          setIsFocused(false);
          register.onBlur(e);
          rest.InputProps?.onBlur?.(e);
        },
      }}
      InputLabelProps={{
        shrink: shouldShrinkOnValue ? !!value || isFocused : undefined,
        ...InputLabelProps,
      }}
      className={clsx(className, shouldWarn && classes.warnedInput)}
    />
  );
};

export const BasicInput: FC<Omit<InputProps, "multiline">> = (props) => {
  const {className, noDefaultStyles = false, ...rest} = props;
  const classes = useStyles();
  return (
    <Input
      className={clsx(className, !noDefaultStyles && classes.input)}
      {...rest}
    />
  );
};

export const MultilineInput: FC<InputProps & {autoHeight?: boolean}> = (
  props
) => {
  const classes = useStyles();
  const {inputProps = {}, autoHeight, className, rows = 6, ...rest} = props;
  return (
    <Input
      className={clsx(classes.multilineInput, className)}
      multiline
      rows={autoHeight ? undefined : rows}
      inputProps={{
        ...inputProps,
        className: clsx(
          {[classes.multilineTextarea]: !autoHeight},
          inputProps?.className
        ),
      }}
      {...rest}
    />
  );
};

export const Content: FC = ({children}) => {
  const classes = useStyles();
  return <div className={classes.content}>{children}</div>;
};

export const SwitchWithLabel: FC<SwitchProps> = (props) => {
  const form = useContext(FormContext) as UseFormReturn;
  const {register, getValues, setValue} = form;

  const {label, name, labelClassName, ...rest} = props;
  const id = name || camelCase(label?.toString() || "");
  const defaultChecked = getValues(name || id);

  // A caller that passes `checked` explicitly wants this switch fully
  // controlled from the outside (e.g. reflecting a value it can also reset
  // programmatically). Mixing that with react-hook-form's own `register()`
  // wiring — which tracks the field uncontrolled via a DOM ref — puts two
  // sources of truth on the same native input and can make a real, external
  // click get silently reverted on the next unrelated re-render.
  const inputProps =
    rest.checked !== undefined
      ? {
          onChange: (
            event: React.ChangeEvent<HTMLInputElement>,
            checked: boolean
          ) => {
            setValue(id, checked, {shouldDirty: true, shouldTouch: true});
            rest.onChange?.(event, checked);
          },
        }
      : {
          ...register(id),
          ...(rest.onChange && {onChange: rest.onChange}),
          defaultChecked: !!defaultChecked,
        };

  return (
    <FormControlLabel
      control={<MuiSwitch {...rest} id={id} {...inputProps} />}
      className={labelClassName}
      label={label}
    />
  );
};

export const Switch: FC<SwitchProps> = (props) => {
  const {onChange} = props;
  const form = useContext(FormContext) as UseFormReturn;
  const {register, getValues} = form;

  const {label, name, ...rest} = props;
  const id = name || camelCase(label?.toString() || "");
  const registerParams = register(id);
  const defaultChecked = getValues(id);

  return (
    <MuiSwitch
      defaultChecked={defaultChecked}
      {...rest}
      id={id}
      {...registerParams}
      onChange={(e, checked) => {
        registerParams.onChange(e);
        if (onChange) {
          onChange(e, checked);
        }
      }}
    />
  );
};

export const Checkbox: FC<CheckboxProps> = (props) => {
  const form = useContext(FormContext) as UseFormReturn;

  const {label, name, formControlLabelProps, ...rest} = props;
  const id = name || camelCase(label?.toString() || "");

  const defaultValue = useMemo(
    () => rest.defaultChecked || form.getValues(id),
    []
  );

  return (
    <FormControlLabel
      control={
        <Controller
          name={id}
          control={form.control}
          defaultValue={defaultValue}
          render={({field}) => (
            <MuiCheckbox
              color={props.shouldUsePrimaryColor ? "primary" : undefined}
              defaultChecked={defaultValue}
              {...field}
              {...rest}
            />
          )}
        />
      }
      disabled={rest?.disabled}
      label={label}
      {...formControlLabelProps}
    />
  );
};

export const EmailInput: FC<InputProps> = ({
  label = "email",
  className,
  ...rest
}) => {
  const classes = useStyles();
  return (
    <Input
      {...rest}
      className={clsx(className, classes.input)}
      label={label}
      type="email"
      rules={{
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/,
          message: "Incorrect email",
        },
      }}
      showHelperText
    />
  );
};

export const Inputs: FC = ({children}) => {
  const classes = useStyles();
  return <div className={classes.inputs}>{children}</div>;
};

export const DatePicker: FC<DatePickerProps> = (props) => {
  const classes = useStyles();

  const {label, rules, name = "", format, required = true, ...rest} = props;
  const form = useContext(FormContext) as UseFormReturn;

  const {control, setError} = form;
  const id = name || camelCase(label?.toString() || "");
  const {validate: customValidate, ...restRules} = rules || {};
  return (
    <Controller
      control={control}
      name={id}
      rules={{
        required,
        ...restRules,
        // Always keep the built-in "is this a real date" guard, and merge any
        // caller-provided validate so a custom rule (e.g. date ordering) can't
        // silently drop it.
        validate: {
          validDate: (value) => {
            try {
              // if value is not date, there will be convertation error
              const dateString = new Date(value).toISOString();
              return dateString !== "Invalid date";
            } catch (e) {
              return false;
            }
          },
          ...(typeof customValidate === "function"
            ? {custom: customValidate}
            : customValidate),
        },
      }}
      render={({field, fieldState}) => {
        const {onChange, value, name, ref} = field;

        const onDateFieldChange = (fieldValue: Date | null): void => {
          if (!fieldValue) {
            setError(name, {type: "required"});
          } else {
            onChange(fieldValue);
          }
        };
        return (
          <KeyboardDateTimePicker
            id={id}
            name={name}
            inputRef={ref}
            className={classes.input}
            DialogProps={{
              className: classes.datePicker,
              PaperProps: {
                id: `paper-dialog-${id}`,
              },
            }}
            KeyboardButtonProps={{
              id: `button-${id}`,
            }}
            inputVariant="outlined"
            label={label}
            helperText={fieldState.error?.message || true}
            {...(fieldState.error && {error: true})}
            ampm={false}
            format={format || "dd/MM/yyyy HH:mm"}
            value={value}
            onBlur={() => !value && onChange("Invalid date")}
            {...rest}
            onChange={onDateFieldChange}
          />
        );
      }}
    />
  );
};

export const InputGroup: FC = ({children}) => {
  const classes = useStyles();
  return <div className={classes.inputGroup}>{children}</div>;
};

export const ActionButton: FC<ActionButtonProps> = ({
  onClick = () => null,
  style = {},
  type = "button",
  text,
  formState = null,
  disabled = false,
  showLoader = false,
  ButtonComponent = Button,
  allowClean = false,
  ...rest
}) => {
  const classes = useStyles();
  const form = useContext(FormContext) as UseFormReturn;
  const {isValid, isDirty, isSubmitting} = formState || form.formState;

  const isDisabled =
    type === "submit"
      ? !isValid || (!allowClean && !isDirty) || isSubmitting || disabled
      : disabled;

  const shouldShowLoader = showLoader && isSubmitting && type === "submit";
  return (
    <ButtonComponent
      style={style}
      type={type}
      className={classes.action}
      onClick={onClick}
      disabled={isDisabled}
      color={rest.color || "primary"}
      {...rest}
    >
      {text}
      {shouldShowLoader && (
        <CircularProgress className={classes.loader} size={16} />
      )}
    </ButtonComponent>
  );
};

export const Actions: FC<ActionsProps> = ({
  children,
  className = "",
  style,
  isLeftAction = false,
}) => {
  const classes = useStyles(isLeftAction);
  return (
    <div className={clsx(classes.actions, className)} style={style}>
      {children}
    </div>
  );
};

const Form: FC<FormProps> = (props) => {
  const {
    subTitle = null,
    title = null,
    form,
    style = {},
    children,
    onSubmit,
    className = null,
    noDefaultStyles,
    id,
    classes: propClasses,
  } = props;
  const classes = useStyles();
  const {title: titleClassName, subtitle: subtitleClassName} =
    propClasses || {};

  const renderSubTitle = () => (
    <Typography className={clsx(classes.formSubTitle, subtitleClassName)}>
      {subTitle}
    </Typography>
  );

  const renderTitle = () => (
    <Typography
      variant="h2"
      className={clsx(classes.formTitle, titleClassName)}
    >
      {title}
    </Typography>
  );

  return (
    <form
      style={style}
      className={clsx(!noDefaultStyles && classes.paper, className)}
      onSubmit={onSubmit}
      id={id}
    >
      {subTitle && renderSubTitle()}
      {title && renderTitle()}
      <FormContext.Provider value={form}>{children}</FormContext.Provider>
    </form>
  );
};

// TODO Migrate form titles to title prop
// TODO onSubmit={form.handleSubmit(...)} is used in multiple places.
//  Wire it up in the form component instead
// TODO move input components to separate files and migrate them to TS

export default Form;
export {default as BasicSelect} from "./inputs/BasicSelect";
export {default as AutocompleteSelect} from "./inputs/AutocompleteSelect";
